import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { Clock, ArrowRight, CheckCircle, AlertTriangle, DollarSign, Users, Zap, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";

const ReplaceCRMManualWork = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Replace Manual CRM Work — The End of CRM Admin",
      "description": "Manual CRM work costs your team 260+ hours per rep per year. Here's how AI eliminates it completely.",
      "url": "https://noq5.ai/replace-crm-manual-work",
      "publisher": { "@type": "Organization", "name": "NoQ5", "url": "https://noq5.ai" }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "How much time do sales reps spend on CRM admin?", "acceptedAnswer": { "@type": "Answer", "text": "Studies show sales reps spend 5-8 hours per week on CRM data entry and admin tasks. That's 260-400 hours per year per rep — time that could be spent selling." } },
        { "@type": "Question", "name": "Can AI eliminate manual CRM work?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. AI CRM agents like NoQ5 automate 95%+ of manual CRM tasks including data entry, contact updates, activity logging, and pipeline management. Reps interact with their CRM through voice or chat, and the AI handles all the admin." } }
      ]
    }
  ];

  return (
    <Layout>
      <SEOHead
        title="Replace Manual CRM Work — Eliminate CRM Admin Forever | NoQ5"
        description="Sales reps waste 260+ hours/year on CRM admin. NoQ5 AI agents eliminate manual data entry, updates, and logging — so your team sells instead of typing."
        keywords="replace CRM manual work, eliminate CRM admin, reduce CRM data entry, CRM admin automation, stop manual CRM updates, end CRM data entry, automate CRM admin work"
        canonicalUrl="https://noq5.ai/replace-crm-manual-work"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-background via-background to-destructive/5">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <span className="inline-block px-4 py-2 bg-destructive/10 text-destructive rounded-full text-sm font-semibold mb-6">MANUAL CRM WORK IS DEAD</span>
          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
            Your Sales Team Isn't Paid to<br /><span className="text-primary">Type Into a CRM</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            260 hours per rep, per year. Wasted on CRM admin. That's not a process problem — it's a design flaw. CRMs were built for managers, not sellers. NoQ5 fixes that.
          </p>
          <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground" asChild>
            <Link to="/trial">Eliminate CRM Admin <ArrowRight className="ml-2 h-5 w-5" /></Link>
          </Button>
        </div>
      </section>

      {/* The Cost */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">The Real Cost of Manual CRM Work</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { stat: "5-8hrs", label: "Per rep, per week on CRM admin", icon: Clock },
              { stat: "260hrs", label: "Per rep, per year wasted", icon: AlertTriangle },
              { stat: "$25K+", label: "Annual cost per rep in lost selling time", icon: DollarSign },
              { stat: "30-50%", label: "Of CRM data outdated at any time", icon: Users }
            ].map((item, i) => (
              <div key={i} className="bg-card p-6 rounded-xl border border-border text-center">
                <item.icon className="h-10 w-10 text-primary mx-auto mb-3" />
                <div className="text-3xl font-bold text-foreground mb-1">{item.stat}</div>
                <p className="text-muted-foreground text-sm">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Gets Replaced */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">Every Manual Task NoQ5 Replaces</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Logging calls after every conversation",
              "Writing meeting notes and follow-ups",
              "Updating deal stages and amounts",
              "Creating new contact records",
              "Enriching lead data from LinkedIn",
              "Syncing email threads to deals",
              "Updating forecasts and projections",
              "Cleaning duplicate records",
              "Tracking competitor mentions",
              "Filing activity reports for managers"
            ].map((task, i) => (
              <div key={i} className="flex items-center gap-3 bg-card p-4 rounded-lg border border-border">
                <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                <span className="text-foreground">{task}</span>
                <span className="ml-auto text-xs bg-primary/10 text-primary px-2 py-1 rounded-full font-medium">Automated</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">How NoQ5 Replaces Manual CRM Work</h2>
          <div className="space-y-8">
            {[
              { step: "1", title: "Connect Your CRM", desc: "Link Salesforce, HubSpot, or Pipedrive in 5 minutes. NoQ5 maps your fields, pipelines, and workflows automatically." },
              { step: "2", title: "AI Learns Your Process", desc: "The AI agent observes your team's patterns — how deals move, what fields matter, how notes are structured — and adapts." },
              { step: "3", title: "CRM Runs Itself", desc: "Every email, call, and meeting is captured. Records update. Leads get enriched. Pipeline moves. Zero manual input required." }
            ].map((item, i) => (
              <div key={i} className="flex gap-6 items-start bg-card p-8 rounded-xl border border-border">
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xl flex-shrink-0">{item.step}</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Manual CRM Work Ends Today</h2>
          <p className="text-xl opacity-90 mb-8">Join teams that gave their reps 260 hours back. Let AI handle the CRM.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" asChild>
              <Link to="/trial">Start Free Trial <ArrowRight className="ml-2 h-5 w-5" /></Link>
            </Button>
            <Button size="lg" variant="outline" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10" asChild>
              <Link to="/crm-agent">See CRM Agent</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ReplaceCRMManualWork;
